import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../store';
import { authAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';

export function AuthPage() {
  const navigate = useNavigate();
  const { login, register, isAuthenticated, user, logout } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await authAPI.forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Register state
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'member',
    organization_name: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const data = await login(loginEmail, loginPassword);
      toast.success(`¡Bienvenido, ${data.user.name}!`);
      
      // Redirect based on role
      const roleRedirects = {
        ceo: '/ceo-dashboard',
        manager: '/dashboard',
        member: '/member-dashboard',
        client: '/client-portal',
        collaborator: '/collab',
      };
      navigate(roleRedirects[data.user.role] || '/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerData);
      toast.success('Cuenta creada correctamente. Inicia sesión.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#e9e1d4] via-[#fbf9f5] to-[#c6d8cd] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary mb-4">
            <span className="text-white font-bold text-2xl">K</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Portal Karma Digital</h1>
          <p className="text-muted-foreground mt-2">Gestión empresarial inteligente</p>
        </div>

        <Card className="border-0 shadow-xl">
          {isAuthenticated && user && (
            <div className="m-2 mb-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-center justify-between gap-2" data-testid="active-session-notice">
              <p className="text-xs text-amber-800">
                Ya hay una sesión abierta como <span className="font-semibold">{user.name}</span>. Si entras con otra cuenta, se cerrará esta.
              </p>
              <button
                type="button"
                onClick={logout}
                className="text-xs font-semibold text-amber-900 underline shrink-0"
                data-testid="active-session-logout-btn"
              >
                Cerrar sesión
              </button>
            </div>
          )}
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-1 m-2" style={{ width: 'calc(100% - 16px)' }}>
              <TabsTrigger value="login" data-testid="login-tab">Iniciar Sesión</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="p-6 pt-2">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="tu@email.com"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Contraseña</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-submit-btn">
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Iniciando...
                    </>
                  ) : (
                    'Iniciar Sesión'
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setForgotEmail(loginEmail); setForgotSent(false); }}
                  className="w-full text-center text-xs text-muted-foreground hover:text-primary mt-1"
                  data-testid="forgot-password-link"
                >
                  ¿Has olvidado tu contraseña?
                </button>
              </form>

              {forgotMode && (
                <div className="mt-4 pt-4 border-t" data-testid="forgot-password-panel">
                  {forgotSent ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800" data-testid="forgot-sent-msg">
                      Si el email existe, te hemos enviado un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada (y la carpeta de spam).
                      <Button variant="link" className="px-0 h-auto mt-2 block" onClick={() => setForgotMode(false)} data-testid="forgot-back-btn">Volver al inicio de sesión</Button>
                    </div>
                  ) : (
                    <form onSubmit={handleForgot} className="space-y-3">
                      <p className="text-sm font-medium">Restablecer contraseña</p>
                      <p className="text-xs text-muted-foreground">Introduce tu email y te enviaremos un enlace para crear una nueva contraseña.</p>
                      <Input type="email" placeholder="tu@email.com" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="forgot-email-input" />
                      <div className="flex gap-2">
                        <Button type="submit" className="flex-1" disabled={isLoading} data-testid="forgot-submit-btn">
                          {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar enlace'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setForgotMode(false)} data-testid="forgot-cancel-btn">Cancelar</Button>
                      </div>
                    </form>
                  )}
                </div>
              )}

            </TabsContent>

            {/* Registro público deshabilitado: acceso solo por invitación */}
          </Tabs>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          © 2026 Karma Digital Agency. Todos los derechos reservados.
        </p>
      </motion.div>
    </div>
  );
}
